import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { PageShell } from "@/components/PageShell";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { t, locale } = useLang();
  const [state, setState] = useState({ loading: true, paid: false, credits: 0, err: "" });

  useEffect(() => {
    if (!sid) { setState({ loading: false, paid: false, credits: 0, err: "Missing session id" }); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setState({ loading: false, paid: true, credits: r.data.credits, err: "" });
          return;
        }
        if (r.data.session_status === "expired") {
          setState({ loading: false, paid: false, credits: 0, err: "Session expirée" });
          return;
        }
      } catch (e) {
        if (attempts >= 8) { setState({ loading: false, paid: false, credits: 0, err: "Status error" }); return; }
      }
      attempts++;
      if (attempts < 10) setTimeout(poll, 2000);
      else setState((s) => ({ ...s, loading: false, err: "Timeout" }));
    };
    poll();
  }, [sid]);

  return (
    <PageShell testId="page-payment-success" title={locale === "fr" ? "Paiement" : "Payment"}>
      <div className="glass glow-border rounded-2xl p-10 max-w-2xl">
        {state.loading && (
          <div className="flex items-center gap-3 text-slate-300" data-testid="payment-loading">
            <Loader2 className="animate-spin text-[#00F0FF]" /> {locale === "fr" ? "Vérification du paiement..." : "Verifying payment..."}
          </div>
        )}
        {!state.loading && state.paid && (
          <div data-testid="payment-success-box">
            <div className="flex items-center gap-3 text-[#00F0FF] mb-4">
              <CheckCircle2 size={28} />
              <span className="font-display text-2xl text-amber-glow">{locale === "fr" ? "Crédits ajoutés" : "Credits added"}</span>
            </div>
            <p className="text-slate-300">
              {locale === "fr"
                ? `Vous disposez maintenant de ${state.credits} messages. Reprenez votre conversation à tout moment.`
                : `You now have ${state.credits} messages. Resume your conversation anytime.`}
            </p>
            <Link to="/" className="btn-primary mt-8 inline-flex" data-testid="payment-back-home">{locale === "fr" ? "Retour à l'accueil" : "Back to home"}</Link>
          </div>
        )}
        {!state.loading && !state.paid && (
          <div data-testid="payment-error-box">
            <div className="flex items-center gap-3 text-red-400 mb-4">
              <XCircle size={28} />
              <span className="font-display text-2xl">{locale === "fr" ? "Paiement non confirmé" : "Payment not confirmed"}</span>
            </div>
            <p className="text-slate-400">{state.err || ""}</p>
          </div>
        )}
        <p className="mt-8 text-xs text-slate-500">{t("disclaimer")}</p>
      </div>
    </PageShell>
  );
}
